import {
  PublicClientApplication,
} from "@azure/msal-browser";
import { AuthConfig } from "./config";
import { fetchConfig } from "./api";

let globalAuthConfig: AuthConfig | null = null;

export async function getAuthConfig(): Promise<AuthConfig> {
  if (!globalAuthConfig) {
    const config = await fetchConfig();
    // Prefer a public SPA client id when provided by runtime or local env
    const effectiveSpaClientId = (config.spaClientId as string) || process.env.VUE_APP_AZURE_CLIENT_ID || config.clientId;

    globalAuthConfig = {
      clientId: effectiveSpaClientId,
      tenantId: config.tenantId,
      authority: config.authority as string,
      authScopes: config.authScopes as string[],
      apiScopes: config.apiScopes as string[],
      graphScopes: config.graphScopes as string[]
    };
  }
  return globalAuthConfig as AuthConfig;
}

class MSALService {
  private msalInstance: PublicClientApplication | null = null;
  private initializationPromise: Promise<void> | null = null;

  async initialize(): Promise<void> {
    if (this.initializationPromise) {
      return this.initializationPromise;
    }
    this.initializationPromise = this.doInitialize();
    return this.initializationPromise;
  }

  private async doInitialize(): Promise<void> {
    if (this.msalInstance) return;

    const config = await getAuthConfig();

    // MSAL configuration using dynamic config
    const msalConfig = {
      auth: {
        clientId: config.clientId,
        authority: config.authority,
        redirectUri: window.location.origin,
      },
      cache: {
        cacheLocation: "sessionStorage" as const,
        storeAuthStateInCookie: false,
      },
    };

    console.log("MSAL: initializing PublicClientApplication with clientId=", msalConfig.auth.clientId);
    this.msalInstance = new PublicClientApplication(msalConfig);
    await this.msalInstance.initialize();
  }

  async getInstance(): Promise<PublicClientApplication> {
    await this.initialize();
    if (!this.msalInstance) {
      throw new Error("MSAL instance not initialized");
    }
    return this.msalInstance;
  }

  async getAuthenticationScopes() {
    const config = await getAuthConfig();
    return {
      scopes: config.authScopes,
    };
  }

  async getApiScopes() {
    const config = await getAuthConfig();
    return {
      scopes: config.apiScopes,
    };
  }

  async getGraphScopes() {
    const config = await getAuthConfig();
    return {
      scopes: config.graphScopes,
    };
  }
}

export const msalService = new MSALService();

// Legacy export - use msalService.getInstance() instead
export const msalInstance = msalService.getInstance();
